import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  Button
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ColorPalette from '../../colorpalette/ColorPalette';
import styles from './ExternalIntegrationInfo.module.css';

/**
 * Componente para exibir informações sobre integrações com serviços externos
 * 
 * @param {Object} props
 * @param {string} props.title - Título da integração
 * @param {string|string[]} props.description - Descrição explicativa (string ou array de strings para múltiplos parágrafos)
 * @param {string} props.providerName - Nome do parceiro (ex: "Z-API", "Focus NFe")
 * @param {string} props.providerUrl - URL do site do parceiro
 * @param {string} [props.logo] - URL ou caminho do logo do parceiro (opcional)
 * @param {string} [props.warning] - Texto de observação/aviso adicional (opcional)
 */
function ExternalIntegrationInfo({
  title,
  description,
  providerName,
  providerUrl,
  logo,
  warning
}) {
  // Normalizar description para sempre ser um array
  const descriptionParagraphs = Array.isArray(description) ? description : [description];

  const handleProviderClick = () => {
    window.open(providerUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: ColorPalette.blueLight5,
        borderRadius: '8px',
        padding: '12px 14px',
        border: `1px solid ${ColorPalette.border}`,
        mb: 2
      }}
    >
      {/* Header com logo/ícone e título */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        {/* Logo ou ícone padrão */}
        <Box sx={{ mr: 1.5 }}>
          {logo ? (
            <Box
              component="img"
              src={logo}
              alt={`Logo ${providerName}`}
              className={styles.providerLogo}
            />
          ) : (
            <InfoOutlinedIcon
              sx={{
                color: ColorPalette.info,
                fontSize: 20
              }}
            />
          )}
        </Box>

        {/* Título e badge */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: ColorPalette.textDark,
              fontSize: '0.9rem'
            }}
          >
            {title}
          </Typography>
          <Chip
            label="Serviço Externo"
            size="small"
            sx={{
              backgroundColor: ColorPalette.info,
              color: ColorPalette.white,
              height: '18px',
              '& .MuiChip-label': {
                fontSize: '0.65rem',
                paddingLeft: '6px',
                paddingRight: '6px'
              }
            }}
          />
        </Box>
      </Box>

      {/* Conteúdo descritivo */}
      <Box sx={{ mb: 1.5 }}>
        {descriptionParagraphs.map((paragraph, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{
              color: ColorPalette.textMedium,
              lineHeight: 1.5,
              fontSize: '0.8rem',
              mb: index < descriptionParagraphs.length - 1 ? 1 : 0
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      {/* Warning/observação se fornecida */}
      {warning && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 1.5
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: ColorPalette.textLight,
              fontSize: 16,
              mr: 0.5,
              mt: 0.1
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: ColorPalette.textLight,
              lineHeight: 1.4,
              fontSize: '0.8rem'
            }}
          >
            {warning}
          </Typography>
        </Box>
      )}

      {/* Botão de acesso ao site do provedor */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleProviderClick}
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            color: ColorPalette.secondary,
            borderColor: ColorPalette.secondary,
            '&:hover': {
              backgroundColor: ColorPalette.secondaryAlpha,
              borderColor: ColorPalette.secondaryDark
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            padding: '4px 12px',
            minHeight: 'auto'
          }}
        >
          Acessar site da {providerName}
        </Button>
      </Box>
    </Paper>
  );
}

export default ExternalIntegrationInfo;